import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    Fragment,
    useState,
    type FC
} from "react";
import type ISystemToolbarProps from "./type";
import type {
    ISystemToolbarAction
} from "./type";
import {
    NCoreUIKitLocalize
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import {
    ChevronDown as ChevronDownIcon,
    ChevronUp as ChevronUpIcon
} from "lucide-react-native";
import {
    warnOnce
} from "../../utils/development";
import AnimatedIcon from "../animatedIcon";
import DropdownMenu from "../dropdownMenu";
import Button from "../button";

const SystemToolbarOpenContext = createContext<boolean>(false);

const SystemToolbarChevronIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => {
    const isOpen = useContext(SystemToolbarOpenContext);

    return <AnimatedIcon
        fromIcon={ChevronDownIcon}
        customColor={customColor}
        isTransitioned={isOpen}
        toIcon={ChevronUpIcon}
        iconTransition="morph"
        color={color}
        size={size}
    />;
};

const getIsTitlePropsUsed = ({
    actions
}: {
    actions?: ISystemToolbarAction[];
}): boolean => {
    if(!actions) {
        return false;
    }

    return actions.some((action) => action.titleProps !== undefined || getIsTitlePropsUsed({
        actions: action.content
    }));
};

const renderActionItems = ({
    keyPrefix,
    localize,
    actions
}: {
    localize: ReturnType<typeof NCoreUIKitLocalize.useContext>["localize"];
    actions: ISystemToolbarAction[];
    keyPrefix: string;
}) => actions.map((action, index) => {
    const key = `${keyPrefix}-${index}`;

    const actionTitle = action.title ?? (action.titleKey ? localize(action.titleKey) : "");

    const separator = action.isSeparatorBefore ? <DropdownMenu.Separator
        key={`${key}-separator`}
    /> : null;

    if(!action.content || action.content.length === 0) {
        return <Fragment
            key={key}
        >
            {separator}
            <DropdownMenu.Item
                disabledReason={action.disabledReason}
                isDisabled={action.isDisabled}
                shortcut={action.shortcut}
                onPress={action.onPress}
                style={action.style}
                title={actionTitle}
                icon={action.icon}
                type={action.type}
            />
        </Fragment>;
    }

    return <Fragment
        key={key}
    >
        {separator}
        <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger
                disabledReason={action.disabledReason}
                isDisabled={action.isDisabled}
                style={action.style}
                title={actionTitle}
                icon={action.icon}
            />
            <DropdownMenu.SubContent>
                {renderActionItems({
                    keyPrefix: `${key}-child`,
                    actions: action.content,
                    localize
                })}
            </DropdownMenu.SubContent>
        </DropdownMenu.Sub>
    </Fragment>;
});

const SystemToolbar: FC<ISystemToolbarProps> = ({
    variant = "ghost",
    type = "neutral",
    size = "medium",
    customLocalize,
    onOpenChange,
    customTheme,
    isDisabled,
    titleKey,
    actions,
    onPress,
    style,
    title,
    icon
}) => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const [
        isOpen,
        setIsOpen
    ] = useState(false);

    const mainTitle = titleKey ? localize(titleKey) : title;

    const hasActions = actions && actions.length > 0;

    const isTitlePropsUsed = getIsTitlePropsUsed({
        actions
    });

    useEffect(() => {
        if(!isTitlePropsUsed) {
            return;
        }

        warnOnce({
            message: "SystemToolbar: \"titleProps\" of an action currently has no effect. The title of a menu item can't be styled yet."
        });
    }, [
        isTitlePropsUsed
    ]);

    const handleOpenChange = useCallback((nextIsOpen: boolean) => {
        setIsOpen(nextIsOpen);

        onOpenChange?.(nextIsOpen);
    }, [
        onOpenChange
    ]);

    const handlePress = () => {
        onPress?.();
    };

    // Dropdown triggers typically don't have a main action unless specified.
    // Wait, we don't have onPress on the root SystemToolbar based on user request.
    const triggerBtn = <Button
        icon={icon ?? (hasActions ? SystemToolbarChevronIcon : undefined)}
        iconDirection={icon || !hasActions ? "start" : "end"}
        customTheme={customTheme}
        isDisabled={isDisabled}
        onPress={handlePress}
        variant={variant}
        title={mainTitle}
        style={style}
        size={size}
        type={type}
    />;

    if(!hasActions) {
        return triggerBtn;
    }

    return <SystemToolbarOpenContext.Provider
        value={isOpen}
    >
        <DropdownMenu
            customLocalize={customLocalize}
            onOpenChange={handleOpenChange}
            customTheme={customTheme}
            title={mainTitle}
            isOpen={isOpen}
            align="start"
            side="bottom"
        >
            <DropdownMenu.Trigger
                isDisabled={isDisabled}
            >
                {triggerBtn}
            </DropdownMenu.Trigger>
            <DropdownMenu.Content>
                {renderActionItems({
                    keyPrefix: "system-toolbar-action",
                    localize,
                    actions
                })}
            </DropdownMenu.Content>
        </DropdownMenu>
    </SystemToolbarOpenContext.Provider>;
};
export default SystemToolbar;
